const BLOCKED_TIMEOUT_MS = 3000;

/**
 * Wraps `indexedDB.deleteDatabase` so a lingering open connection elsewhere
 * in the app can never leave the caller hanging forever. If the browser
 * reports the deletion is blocked, we still resolve after a short grace
 * period so the rest of a reset flow (caches, service worker, storage) can
 * proceed — the database row is removed as soon as the blocking connection
 * closes.
 */
export function deleteDatabaseSafely(
  name: string,
  indexedDBRef: IDBFactory = indexedDB
): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const settle = () => {
      if (settled) {
        return;
      }
      settled = true;
      resolve();
    };

    let request: IDBOpenDBRequest;
    try {
      request = indexedDBRef.deleteDatabase(name);
    } catch (error) {
      console.error("Failed to delete database:", error);
      settle();
      return;
    }

    request.onsuccess = () => settle();
    request.onerror = () => {
      console.error("Failed to delete database:", request.error);
      settle();
    };
    request.onblocked = () => {
      console.warn(
        `Database deletion blocked, continuing after ${BLOCKED_TIMEOUT_MS}ms grace period`
      );
      setTimeout(settle, BLOCKED_TIMEOUT_MS);
    };
  });
}
